import React, { useEffect, useMemo, useState } from "react";
import { Activity, BarChart3, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { GetLLMFilters, GetLLMLogById, GetLLMLogs, GetLLMStats } from "./apis/get";

type SortOrder = "asc" | "desc";

const SORTABLE_COLUMNS = [
  "id",
  "created_at",
  "total_tokens",
  "total_cost",
  "latency_ms",
  "status",
  "model_name",
  "provider",
];

const defaultQuery = {
  page: 1,
  limit: 5,
  offset: 0,
  sort_by: "created_at",
  sort_order: "desc" as SortOrder,
  source: "",
  feature: "",
  provider: "",
  model_name: "",
  api_type: "",
  status: "",
  user_id: "",
  candidate_id: "",
  request_id: "",
  trace_id: "",
  session_id: "",
  error_code: "",
  http_status: "",
  date_from: "",
  date_to: "",
};

const badgeClass = (status?: string) => {
  if ((status || "").toLowerCase() === "success") return "bg-emerald-500/10 border-emerald-500/30 text-emerald-300";
  if ((status || "").toLowerCase() === "error") return "bg-red-500/10 border-red-500/30 text-red-300";
  return "bg-slate-700/40 border-slate-600 text-slate-300";
};

export default function LLMLogs() {
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(defaultQuery);
  const [logs, setLogs] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState<any>(null);
  const [head, setHead] = useState<any>(null);
  const [billing, setBilling] = useState<any>(null);
  const [filters, setFilters] = useState<any>({});
  const [stats, setStats] = useState<any>(null);
  const [activeLog, setActiveLog] = useState<any>(null);
  const [activeLogLoading, setActiveLogLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchLogs = async () => {
    setLoading(true);
    setError("");
    try {
      const res: any = await GetLLMLogs(query);
      if (res?.status) {
        setLogs(Array.isArray(res.data) ? res.data : []);
        setTotalCount(Number(res.count || 0));
        setPagination(res.pagination || null);
        setHead(res.head || null);
        setBilling(res.billing || null);
      } else {
        setError(res?.message || "Failed to fetch LLM logs");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to fetch LLM logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const statsQuery = {
      ...query,
      group_by: "model_name",
      granularity: "day",
    };
    try {
      const res: any = await GetLLMStats(statsQuery);
      if (res?.status) {
        setStats(res.data || null);
      }
    } catch {
      // keep logs page functional even if stats fails
    }
  };

  const fetchFilters = async () => {
    try {
      const params: any = {};
      if (query.date_from) params.date_from = query.date_from;
      if (query.date_to) params.date_to = query.date_to;
      const res: any = await GetLLMFilters(params);
      if (res?.status) {
        setFilters(res.data || {});
      }
    } catch {
      setFilters({});
    }
  };

  useEffect(() => {
    fetchFilters();
  }, [query.date_from, query.date_to]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [query]);

  const currentPage = pagination?.page || query.page || 1;
  const totalPages = pagination?.total_pages || Math.max(1, Math.ceil(totalCount / query.limit));

  const updateQuery = (patch: Partial<typeof defaultQuery>, resetPage = false) => {
    setQuery((prev) => ({
      ...prev,
      ...patch,
      page: resetPage ? 1 : (patch.page ?? prev.page),
      offset: resetPage ? 0 : (patch.offset ?? prev.offset),
    }));
  };

  const toggleSort = (col: string) => {
    if (!SORTABLE_COLUMNS.includes(col)) return;
    setQuery((prev) => ({
      ...prev,
      sort_by: col,
      sort_order: prev.sort_by === col && prev.sort_order === "desc" ? "asc" : "desc",
      page: 1,
      offset: 0,
    }));
  };

  const openLogDetails = async (id: number) => {
    setActiveLogLoading(true);
    setActiveLog(null);
    try {
      const res: any = await GetLLMLogById(id);
      if (res?.status) {
        setActiveLog(res.data || null);
      } else {
        setActiveLog({ error: res?.message || "LLM log not found" });
      }
    } catch (e: any) {
      setActiveLog({ error: e?.message || "Failed to fetch log details" });
    } finally {
      setActiveLogLoading(false);
    }
  };

  const summary = stats?.summary || {};
  const headSummary = head || {};
  const billingInfo = billing || stats?.billing || null;
  const grouped = Array.isArray(stats?.grouped) ? stats.grouped : [];
  const timeseries = Array.isArray(stats?.timeseries) ? stats.timeseries : [];

  return (
    <div className="min-h-screen p-4 md:p-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">LLM Logs</h1>
          <p className="text-slate-400 text-sm mt-1">Logs, filters, and usage analytics</p>
        </div>
        <button
          onClick={() => {
            fetchLogs();
            fetchStats();
            fetchFilters();
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white text-xs font-bold uppercase tracking-wider hover:bg-slate-800 transition"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Total Calls</p>
          <p className="text-2xl font-black text-white mt-1">{headSummary.total_calls ?? summary.total_calls ?? 0}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Total Tokens</p>
          <p className="text-2xl font-black text-white mt-1">{headSummary.total_tokens ?? summary.total_tokens ?? 0}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Total Cost</p>
          <p className="text-2xl font-black text-white mt-1">${headSummary.total_cost ?? summary.total_cost ?? "0.00000000"}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Avg Latency</p>
          <p className="text-2xl font-black text-white mt-1">{headSummary.avg_latency_ms ?? summary.avg_latency_ms ?? 0} ms</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Current Page</p>
          <p className="text-2xl font-black text-white mt-1">{currentPage}/{totalPages}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Input Tokens</p>
          <p className="text-lg font-black text-slate-100 mt-1">{headSummary.input_tokens ?? summary.input_tokens ?? 0}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Output Tokens</p>
          <p className="text-lg font-black text-slate-100 mt-1">{headSummary.output_tokens ?? summary.output_tokens ?? 0}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Input Cost</p>
          <p className="text-lg font-black text-slate-100 mt-1">${headSummary.input_cost ?? summary.input_cost ?? "0.00000000"}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Output Cost</p>
          <p className="text-lg font-black text-slate-100 mt-1">${headSummary.output_cost ?? summary.output_cost ?? "0.00000000"}</p>
        </div>
      </div>

      {billingInfo && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-xl border border-slate-800 bg-cyan-900/20 p-3">
            <p className="text-[10px] uppercase tracking-widest text-cyan-300/70">Total Spend</p>
            <p className="text-lg font-black text-cyan-100 mt-1">
              {billingInfo.currency || "USD"} {billingInfo.total_spend ?? "0.00000000"}
            </p>
          </div>
          {billingInfo.budget !== null && billingInfo.budget !== undefined && (
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Budget</p>
              <p className="text-lg font-black text-slate-100 mt-1">
                {billingInfo.currency || "USD"} {billingInfo.budget}
              </p>
            </div>
          )}
          {billingInfo.remaining_balance !== null && billingInfo.remaining_balance !== undefined && (
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Remaining</p>
              <p className="text-lg font-black text-slate-100 mt-1">
                {billingInfo.currency || "USD"} {billingInfo.remaining_balance}
              </p>
            </div>
          )}
          {billingInfo.utilization_percent !== null && billingInfo.utilization_percent !== undefined && (
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Utilization</p>
              <p className="text-lg font-black text-slate-100 mt-1">
                {Number(billingInfo.utilization_percent).toFixed(2)}%
              </p>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <select value={query.source} onChange={(e) => updateQuery({ source: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm">
          <option value="">All Sources</option>
          {(filters.sources || []).map((v: string) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={query.feature} onChange={(e) => updateQuery({ feature: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm">
          <option value="">All Features</option>
          {(filters.features || []).map((v: string) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={query.provider} onChange={(e) => updateQuery({ provider: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm">
          <option value="">All Providers</option>
          {(filters.providers || []).map((v: string) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={query.model_name} onChange={(e) => updateQuery({ model_name: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm">
          <option value="">All Models</option>
          {(filters.model_names || []).map((v: string) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={query.status} onChange={(e) => updateQuery({ status: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm">
          <option value="">All Statuses</option>
          {(filters.statuses || []).map((v: string) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={String(query.limit)} onChange={(e) => updateQuery({ limit: Number(e.target.value) as any }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm">
          <option value="5">5 / page</option>
          <option value="10">10 / page</option>
          <option value="25">25 / page</option>
          <option value="50">50 / page</option>
          <option value="100">100 / page</option>
          <option value="200">200 / page</option>
        </select>
        <input type="datetime-local" value={query.date_from} onChange={(e) => updateQuery({ date_from: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm" />
        <input type="datetime-local" value={query.date_to} onChange={(e) => updateQuery({ date_to: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm" />
        <input placeholder="user_id" value={query.user_id} onChange={(e) => updateQuery({ user_id: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm" />
        <input placeholder="request_id" value={query.request_id} onChange={(e) => updateQuery({ request_id: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm" />
        <input placeholder="trace_id" value={query.trace_id} onChange={(e) => updateQuery({ trace_id: e.target.value }, true)} className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-200 text-sm" />
        <button onClick={() => setQuery(defaultQuery)} className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-black uppercase tracking-wide">
          Reset
        </button>
      </div>

      {error && <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-rose-300 text-sm">{error}</div>}

      <div className="rounded-2xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80">
              <tr>
                {["id", "created_at", "provider", "model_name", "feature", "status", "total_tokens", "total_cost", "latency_ms"].map((col) => (
                  <th key={col} className="px-4 py-3 text-left text-xs font-black uppercase tracking-wider text-slate-400">
                    <button onClick={() => toggleSort(col)} className="hover:text-white transition-colors">
                      {col}
                    </button>
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wider text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-slate-950">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-500">Loading logs...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-500">No LLM logs found</td></tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-t border-slate-800 hover:bg-slate-900/50">
                    <td className="px-4 py-3 text-slate-300 font-mono">#{log.id}</td>
                    <td className="px-4 py-3 text-slate-400">{log.created_at ? new Date(log.created_at).toLocaleString() : "-"}</td>
                    <td className="px-4 py-3 text-slate-300">{log.provider || "-"}</td>
                    <td className="px-4 py-3 text-slate-300">{log.model_name || "-"}</td>
                    <td className="px-4 py-3 text-slate-300">{log.feature || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full border text-xs font-bold uppercase ${badgeClass(log.status)}`}>
                        {log.status || "unknown"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{log.total_tokens ?? 0}</td>
                    <td className="px-4 py-3 text-slate-300">${log.total_cost ?? "0.00000000"}</td>
                    <td className="px-4 py-3 text-slate-300">{log.latency_ms ?? 0}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => openLogDetails(log.id)} className="px-2 py-1 rounded-md bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 text-xs font-bold uppercase">
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">Showing {logs.length} of {totalCount}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => updateQuery({ page: Math.max(1, currentPage - 1), offset: Math.max(0, query.offset - query.limit) })}
            disabled={pagination ? !pagination.has_prev : currentPage <= 1}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-200 text-xs font-bold disabled:opacity-50"
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <button
            onClick={() => updateQuery({ page: currentPage + 1, offset: query.offset + query.limit })}
            disabled={pagination ? !pagination.has_next : query.offset + query.limit >= totalCount}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-200 text-xs font-bold disabled:opacity-50"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <p className="text-xs font-semibold text-slate-200 mb-3 flex items-center gap-2"><BarChart3 size={14} /> Grouped Breakdown ({stats?.group_by || "model_name"})</p>
          <div className="overflow-auto max-h-[320px]">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-400 border-b border-slate-800">
                  <th className="text-left py-2">Label</th>
                  <th className="text-right py-2">Calls</th>
                  <th className="text-right py-2">Tokens</th>
                  <th className="text-right py-2">Cost</th>
                </tr>
              </thead>
              <tbody>
                {grouped.length === 0 ? (
                  <tr><td colSpan={4} className="py-3 text-slate-500">No grouped data</td></tr>
                ) : grouped.map((g: any, idx: number) => (
                  <tr key={`${g.label}-${idx}`} className="border-b border-slate-800/50">
                    <td className="py-2 text-slate-200">{g.label || "N/A"}</td>
                    <td className="py-2 text-right text-slate-300">{g.calls ?? 0}</td>
                    <td className="py-2 text-right text-slate-300">{g.total_tokens ?? 0}</td>
                    <td className="py-2 text-right text-slate-300">${g.total_cost ?? "0.00000000"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <p className="text-xs font-semibold text-slate-200 mb-3 flex items-center gap-2"><Activity size={14} /> Time Series ({stats?.granularity || "day"})</p>
          <div className="overflow-auto max-h-[320px]">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-400 border-b border-slate-800">
                  <th className="text-left py-2">Bucket</th>
                  <th className="text-right py-2">Calls</th>
                  <th className="text-right py-2">Tokens</th>
                  <th className="text-right py-2">Cost</th>
                </tr>
              </thead>
              <tbody>
                {timeseries.length === 0 ? (
                  <tr><td colSpan={4} className="py-3 text-slate-500">No time-series data</td></tr>
                ) : timeseries.map((t: any, idx: number) => (
                  <tr key={`${t.bucket}-${idx}`} className="border-b border-slate-800/50">
                    <td className="py-2 text-slate-200">{t.bucket ? new Date(t.bucket).toLocaleString() : "-"}</td>
                    <td className="py-2 text-right text-slate-300">{t.calls ?? 0}</td>
                    <td className="py-2 text-right text-slate-300">{t.total_tokens ?? 0}</td>
                    <td className="py-2 text-right text-slate-300">${t.total_cost ?? "0.00000000"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {(activeLogLoading || activeLog) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99999] flex items-center justify-center p-4" onClick={() => { setActiveLog(null); setActiveLogLoading(false); }}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-4 flex items-center justify-between">
              <h3 className="text-lg font-black text-white uppercase">LLM Log Details</h3>
              <button onClick={() => { setActiveLog(null); setActiveLogLoading(false); }} className="text-slate-400 hover:text-white">Close</button>
            </div>
            <div className="p-4">
              {activeLogLoading ? (
                <div className="text-slate-400 text-sm">Loading details...</div>
              ) : activeLog?.error ? (
                <div className="text-rose-300 text-sm">{activeLog.error}</div>
              ) : (
                <pre className="text-xs text-slate-200 bg-slate-950 border border-slate-800 rounded-xl p-4 overflow-auto whitespace-pre-wrap">
                  {JSON.stringify(activeLog, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
